import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import TwoFactorVerification from '../components/Auth/TwoFactorVerification';
import Logo from '../components/UI/Logo';
import { AtSymbolIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { forceLightTheme } from '../utils/themeUtils';
import { useTranslation } from 'react-i18next';

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { login } = useAuth();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  // Force light theme on login page
  useEffect(() => {
    forceLightTheme();
  }, []);

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    try {
      const result = await login(data.username, data.password);
      if (result.requires2FA && result.tempToken) {
        setRequires2FA(true);
        setTempToken(result.tempToken);
        setErrorMessage(null);
        return;
      }

      if (!result.success) {
        // Mensagem genérica por orientação de segurança (não revelar se e-mail existe)
        setErrorMessage(t('auth.errors.invalidCredentials'));
      } else {
        setErrorMessage(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTempToken('');
  };

  if (requires2FA) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <TwoFactorVerification tempToken={tempToken} onBack={handleBack} />
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <div className='grid grid-cols-1 lg:grid-cols-2 min-h-screen'>
        {/* Painel de Branding (lado esquerdo em telas grandes) */}
        <div className='hidden lg:flex relative overflow-hidden'>
          <div className='absolute inset-0 bg-gradient-to-br from-primary-50 via-blue-50 to-indigo-100 dark:from-primary-900/30 dark:via-indigo-900/20 dark:to-indigo-900/30' />
          <div className='relative z-10 flex flex-col justify-center items-center px-12 w-full'>
            <div className='flex items-center space-x-4 mb-8'>
              <Logo className='h-12 w-auto' alt='Mermaid Logo' />
              <span className='text-2xl font-bold text-gray-900 dark:text-gray-100'>Mermaid</span>
            </div>
            <h1 className='text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-4 text-center'>
              Inteligência de Ameaças Corporativa
            </h1>
            <p className='text-gray-600 dark:text-gray-300 max-w-xl text-center'>
              Conecte-se para acessar dashboards, investigações, relatórios e gestão de segurança com a plataforma
              Mermaid CTI.
            </p>
            <div className='mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl'>
              <div className='rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/50 p-4'>
                <div className='text-sm font-semibold text-gray-700 dark:text-gray-200'>Dashboards</div>
                <div className='text-xs text-gray-500 dark:text-gray-400'>Visão executiva de riscos e eventos</div>
              </div>
              <div className='rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/50 p-4'>
                <div className='text-sm font-semibold text-gray-700 dark:text-gray-200'>Investigação</div>
                <div className='text-xs text-gray-500 dark:text-gray-400'>Domínios, credenciais e ativos</div>
              </div>
              <div className='rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/50 p-4'>
                <div className='text-sm font-semibold text-gray-700 dark:text-gray-200'>Gestão</div>
                <div className='text-xs text-gray-500 dark:text-gray-400'>Usuários, empresas e regras</div>
              </div>
              <div className='rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/50 p-4'>
                <div className='text-sm font-semibold text-gray-700 dark:text-gray-200'>Relatórios</div>
                <div className='text-xs text-gray-500 dark:text-gray-400'>Resultados e KPIs mensais</div>
              </div>
            </div>
          </div>
        </div>

        {/* Card de Login (lado direito) */}
        <div className='flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
          <div className='w-full max-w-md'>
            <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl rounded-2xl p-8'>
              <div className='mb-8 text-center'>
                <Logo className='mx-auto h-14 w-auto' alt='Mermaid Logo' />
                <h2 className='mt-6 text-2xl font-bold text-gray-900 dark:text-gray-100'>Bem-vindo de volta</h2>
                <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>Acesse sua conta para continuar</p>
              </div>

              <form className='space-y-6' onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label htmlFor='username' className='sr-only'>
                    E-mail
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <AtSymbolIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                    </div>
                    <input
                      {...register('username', {
                        required: 'E-mail é obrigatório',
                        onChange: () => errorMessage && setErrorMessage(null),
                      })}
                      id='username'
                      type='email'
                      autoComplete='email'
                      className='appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent sm:text-sm'
                      placeholder='seu.email@empresa.com'
                    />
                  </div>
                  {errors.username && <p className='mt-1 text-sm text-red-600'>{errors.username.message}</p>}
                </div>

                <div>
                  <label htmlFor='password' className='sr-only'>
                    Senha
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <LockClosedIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                    </div>
                    <input
                      {...register('password', {
                        required: 'Senha é obrigatória',
                        onChange: () => errorMessage && setErrorMessage(null),
                      })}
                      id='password'
                      type='password'
                      autoComplete='current-password'
                      className='appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent sm:text-sm'
                      placeholder='Sua senha'
                    />
                  </div>
                  {errors.password && <p className='mt-1 text-sm text-red-600'>{errors.password.message}</p>}
                </div>

                <div className='flex items-center justify-between'>
                  <label className='flex items-center text-sm text-gray-600 dark:text-gray-300'>
                    <input
                      type='checkbox'
                      className='mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500'
                    />
                    Lembrar de mim
                  </label>
                  <Link
                    to='/forgot-password'
                    className='text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300'
                  >
                    Esqueceu a senha?
                  </Link>
                </div>

                {errorMessage && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="rounded-md bg-red-50 border border-red-200 p-3"
                  >
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                )}

                <button
                  type='submit'
                  disabled={loading}
                  className='w-full flex justify-center py-2 px-4 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {loading ? <LoadingSpinner size='sm' className='mr-2' /> : null}
                  {loading ? 'Entrando...' : 'Entrar'}
                </button>
              </form>

              <div className='mt-6 text-center'>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Acesso restrito. Contate o administrador para suporte.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
